import React, { useEffect, useState } from "react";
import AttachmentPanel from "../components/AttachmentPanel";
import { useCallForHelpStore } from "../hooks/useCallForHelpStore";
import { PageHeader, UnsavedChangesDialog } from "@/components/common";
import { useTran, useUnsavedChanges } from "@/hooks";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { Headset, Plus } from "lucide-react";
import { SelectField, InputField } from "@/components/common";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import QuestionInput from "@/features/survey-management/components/QuestionInput";
import { useAuthStore } from "@/features/auth";
import { Button } from "@/components/ui/button";
import { resolveRoleLabel } from "@/features/user-management/utils/user-role-options";
import { SITES_OPTIONS } from "@/features/survey-management/utils/survey-manager.helper";
import ThankYouModal from "../components/ThankYouModal";
import { Can } from "@/hooks/useCan/Can";

const ARRAY_QUESTION_TYPES = ["multiple_dropdown"];

const isAnswerEmpty = (value, questionType) => {
  if (ARRAY_QUESTION_TYPES.includes(questionType)) {
    return !Array.isArray(value) || value.length === 0;
  }
  return value === undefined || value === null || String(value).trim() === "";
};

const CallForHelpPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const store = useCallForHelpStore();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const roleName =
    resolveRoleLabel(user.role, t, undefined, user.customRoleName) ||
    t("Not available");

  useEffect(() => {
    store.initialize();
  }, []);

  const questions = store.selectedSurvey?.questions ?? [];

  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [questionErrors, setQuestionErrors] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);

  const isDirty =
    !!store.emailAddress ||
    Object.keys(store.answers).length > 0 ||
    (store.evidenceFiles && store.evidenceFiles.length > 0);
  const { showDialog, confirmDiscard, cancelDiscard } =
    useUnsavedChanges(isDirty);

  const validateEmail = (value) => {
    if (!value || value.trim() === "") return "Email address is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
      return "Please enter a valid email address.";
    return "";
  };

  const validateQuestions = () => {
    const errors = {};
    questions.forEach((question) => {
      if (question.required) {
        const rawValue = store.answers[question._id];
        if (isAnswerEmpty(rawValue, question.questionType)) {
          errors[question._id] = "This field is required.";
        }
      }
    });
    return errors;
  };

  const handleAnswerChange = (questionId, val, questionType) => {
    store.setAnswer(questionId, val);
    if (questionErrors[questionId] && !isAnswerEmpty(val, questionType)) {
      setQuestionErrors((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  const handleEmailChange = (e) => {
    store.setEmailAddress(e.target.value);
    if (emailTouched) setEmailError(validateEmail(e.target.value));
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(store.emailAddress));
  };

  const handleSubmit = async () => {
    setEmailTouched(true);
    const emailErr = validateEmail(store.emailAddress);
    setEmailError(emailErr);

    const qErrors = validateQuestions();
    setQuestionErrors(qErrors);

    if (emailErr || Object.keys(qErrors).length > 0) return;

    runInAction(() => {
      store.submitting = true;
    });
    try {
      const answersArray = Object.entries(store.answers).map(
        ([questionId, answer]) => {
          const q = (store.selectedQuestions ?? []).find(
            (q) => q._id === questionId,
          );
          return {
            questionId,
            questionText: q?.questionText ?? "",
            questionType: q?.questionType ?? "text",
            category: q?.category ?? "common",
            options: q?.options ?? [],
            answer,
          };
        },
      );

      const payload = {
        surveyId: store.feedbackType,
        emailAddress: store.emailAddress,
        site: store.site,
        respondent: {
          name: user?.name || "",
          email: user?.email || "",
          company: user?.companyName || "",
          jobTitle: roleName,
        },
        answers: answersArray,
        submittedAt: new Date().toISOString(),
        metadata: {
          userAgent: navigator.userAgent,
          locale: navigator.language,
        },
        attachments: store.evidenceFiles,
      };
      const { success } = await store.handleSubmit(payload);
      if (success) {
        // ── Reset form ──
        setEmailError("");
        setEmailTouched(false);
        setQuestionErrors({});
        runInAction(() => {
          store.answers = {};
          store.emailAddress = "";
        });
        store.resetAttachments();
        setShowThankYou(true);
      }
    } catch (err) {
      console.error("Call For Help Submit failed:", err);
    } finally {
      runInAction(() => {
        store.submitting = false;
      });
    }
  };

  if (store.initializing) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("Loading...")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
      <PageHeader
        title={t("Customer Feedback Form")}
        description="Submit your feedback or request assistance from our support team"
        icon={Headset}
      />

      <div className="flex gap-4 items-stretch">
        {/* ══════════ LEFT: Main Form ══════════ */}
        <div
          className="flex-1 min-w-0 p-4 rounded-lg border
                     bg-white border-gray-200
                     dark:bg-gray-900 dark:border-gray-700"
        >
          {/* ── Empty State ── */}
          {!store.loading && store.surveys.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <Headset className="w-12 h-12 text-gray-300 dark:text-gray-600" />
              <p className="text-base font-medium text-gray-500 dark:text-gray-400">
                {t("No survey found")}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t("There are no published surveys available at this time.")}
              </p>
              <Can
                resource="survey_library"
                action="create"
                userPermissions={permissions}
              >
                <Button
                  type="button"
                  size="sm"
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate(PATHS.CREATE_NEW_SURVEY)}
                >
                  <Plus className="h-4 w-4" />
                  {t("Create new survey")}
                </Button>
              </Can>
            </div>
          ) : (
            <>
              {/* ── Top Fields ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Feedback Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t("Type of Feedback")} :
                  </label>
                  <SelectField
                    value={store.feedbackType}
                    onChange={(e) => store.setFeedbackType(e.target.value)}
                    options={store.surveyOptions}
                    className="h-9 text-sm w-full"
                  />
                </div>

                {/* Site */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t("Site")} :
                  </label>
                  <SelectField
                    value={store.site}
                    onChange={(e) => store.setSite(e.target.value)}
                    options={SITES_OPTIONS}
                    className="h-9 text-sm w-full"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                  {" "}
                  {/* ✅ full width on tablet */}
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t("Email Address")}
                    <span className="text-red-500 ml-1">*</span> :
                  </label>
                  <InputField
                    value={store.emailAddress}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={t("Enter your email address...")}
                    className={`w-full ${
                      emailTouched && emailError
                        ? "border-red-400 dark:border-red-500"
                        : emailTouched && !emailError
                          ? "border-green-400 dark:border-green-500"
                          : ""
                    }`}
                  />
                  <div className="min-h-[16px]">
                    {emailTouched && emailError && (
                      <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
                        <span>⚠</span> {t(emailError)}
                      </p>
                    )}
                    {emailTouched && !emailError && store.emailAddress && (
                      <p className="text-xs text-green-500 dark:text-green-400 flex items-center gap-1">
                        <span>✓</span> {t("Valid email address")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── All Questions ── */}
              <div className="space-y-4">
                {" "}
                {/* ✅ questions have their own spacing */}
                {questions.map((question) => {
                  const isArrayType = ARRAY_QUESTION_TYPES.includes(
                    question.questionType,
                  );
                  const rawValue = store.answers[question._id];
                  const value = isArrayType
                    ? Array.isArray(rawValue)
                      ? [...rawValue]
                      : []
                    : (rawValue ?? "");
                  const qError = questionErrors[question._id];

                  return (
                    <div key={question._id} className="space-y-2">
                      <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {question.questionText}
                        {question.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <div
                        className={
                          qError
                            ? "rounded-md ring-1 ring-red-400 dark:ring-red-500"
                            : ""
                        }
                      >
                        <QuestionInput
                          question={question}
                          value={value}
                          onChange={(val) =>
                            handleAnswerChange(
                              question._id,
                              val,
                              question.questionType,
                            )
                          }
                        />
                      </div>
                      {qError && (
                        <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
                          <span>⚠</span> {t(qError)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Submit ── */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="secondary"
                  onClick={handleSubmit}
                  disabled={store.submitting}
                >
                  {store.submitting ? t("Submitting...") : t("Submit")}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* ══════════ RIGHT: Attachment Panel ══════════ */}
        {store.surveys.length > 0 && <AttachmentPanel />}
      </div>
      <ThankYouModal
        open={showThankYou}
        onClose={() => setShowThankYou(false)}
      />
    </div>
  );
});

export default CallForHelpPage;
